import { tipImages } from '../../brush/library';
import { BRUSH_PRESET_CATEGORIES } from '../../brush/presets';
import type { BrushPreset } from '../../brush/types';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../../core/blendModes';
import { GRADIENT_TYPES, GRADIENT_TYPE_LABELS } from '../../core/gradient';
import { TONE_RANGE_LABELS } from '../../core/paintFilters';
import { TEXT_ALIGN_OPTIONS, TEXT_FONT_OPTIONS } from '../../core/textFonts';
import {
  isPaintTool,
  type PaintToolId,
  type SelectionCombineMode,
  type ToolId,
  type TransformMode,
  type VectorMode,
} from '../../core/types';
import { adjustBrushHardness, adjustBrushSize } from '../../state/keymap';
import { useAppStore, type AppState } from '../../state/store';
import { setTransformMode } from '../../tools/transform';
import type { PathOp } from '../../vector/types';
import type { ToolMenuEntry, ToolMenuSection } from './types';

const SELECTION_TOOLS = new Set<ToolId>([
  'selectRect',
  'selectEllipse',
  'lasso',
  'lassoPolygon',
  'lassoMagnetic',
  'magicWand',
  'quickSelect',
]);

const VECTOR_TOOLS = new Set<ToolId>([
  'pen',
  'penFree',
  'shapeRect',
  'shapeEllipse',
  'shapeLine',
  'shapePolygon',
]);

const BLEND_TOOLS = new Set<PaintToolId>(['brush', 'pencil']);
const TRANSFER_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser', 'clone']);
const STRENGTH_TOOLS = new Set<PaintToolId>(['blur', 'sharpen', 'smudge', 'sponge']);
const EXPOSURE_TOOLS = new Set<PaintToolId>(['dodge', 'burn']);
const SOURCE_TOOLS = new Set<PaintToolId>(['clone', 'healing']);
const HARDNESS_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser', 'clone', 'healing']);

const SELECTION_MODES: { value: SelectionCombineMode; label: string }[] = [
  { value: 'replace', label: 'New Selection' },
  { value: 'add', label: 'Add to Selection' },
  { value: 'subtract', label: 'Subtract from Selection' },
  { value: 'intersect', label: 'Intersect with Selection' },
  { value: 'exclude', label: 'Exclude Overlapping' },
];

const SIZE_PRESETS = [5, 10, 20, 40, 80, 150, 300];
const OPACITY_PRESETS = [10, 25, 50, 75, 100];
const HARDNESS_PRESETS = [
  { value: 0, label: 'Soft (0%)' },
  { value: 0.5, label: 'Medium (50%)' },
  { value: 1, label: 'Hard (100%)' },
];

function item(
  label: string,
  onSelect: () => void,
  extras: Partial<Extract<ToolMenuEntry, { kind: 'item' }>> = {},
): ToolMenuEntry {
  return { kind: 'item', label, onSelect, ...extras };
}

function sub(label: string, children: ToolMenuEntry[]): ToolMenuEntry {
  return { kind: 'sub', label, children };
}

function sep(): ToolMenuEntry {
  return { kind: 'separator' };
}

function paintSection(tool: PaintToolId, state: AppState): ToolMenuSection {
  const options = state.paintOptions[tool];
  const patch = (patchValue: Parameters<AppState['patchPaintOptions']>[1]) =>
    useAppStore.getState().patchPaintOptions(tool, patchValue);

  const entries: ToolMenuEntry[] = [
    item('Increase Size', () => adjustBrushSize(1), { shortcut: ']' }),
    item('Decrease Size', () => adjustBrushSize(-1), { shortcut: '[' }),
    sub(
      'Size',
      SIZE_PRESETS.map((size) =>
        item(`${size} px`, () => patch({ size }), { checked: options.size === size }),
      ),
    ),
  ];

  if (HARDNESS_TOOLS.has(tool)) {
    entries.push(
      sep(),
      item('Increase Hardness', () => adjustBrushHardness(0.1), { shortcut: 'Shift+]' }),
      item('Decrease Hardness', () => adjustBrushHardness(-0.1), { shortcut: 'Shift+[' }),
      sub(
        'Hardness',
        HARDNESS_PRESETS.map(({ value, label }) =>
          item(label, () => patch({ hardness: value }), {
            checked: Math.abs(options.hardness - value) < 0.05,
          }),
        ),
      ),
    );
  }

  if (TRANSFER_TOOLS.has(tool)) {
    entries.push(
      sep(),
      sub(
        'Opacity',
        OPACITY_PRESETS.map((pct) =>
          item(`${pct}%`, () => patch({ opacity: pct / 100 }), {
            checked: Math.round(options.opacity * 100) === pct,
          }),
        ),
      ),
      sub(
        'Flow',
        OPACITY_PRESETS.map((pct) =>
          item(`${pct}%`, () => patch({ flow: pct / 100 }), {
            checked: Math.round(options.flow * 100) === pct,
          }),
        ),
      ),
    );
  }

  if (BLEND_TOOLS.has(tool)) {
    entries.push(
      sep(),
      sub(
        'Blend Mode',
        BLEND_MODE_GROUPS.flat().map((mode) =>
          item(BLEND_MODE_LABELS[mode], () => patch({ blendMode: mode }), {
            checked: options.blendMode === mode,
          }),
        ),
      ),
    );
  }

  if (STRENGTH_TOOLS.has(tool)) {
    entries.push(
      sep(),
      sub(
        'Strength',
        OPACITY_PRESETS.map((pct) =>
          item(`${pct}%`, () => patch({ strength: pct / 100 }), {
            checked: Math.round((options.strength ?? 0.5) * 100) === pct,
          }),
        ),
      ),
    );
  }

  if (tool === 'sponge') {
    entries.push(
      sep(),
      item('Saturate', () => patch({ sponge: 'saturate' }), {
        checked: options.sponge === 'saturate',
      }),
      item('Desaturate', () => patch({ sponge: 'desaturate' }), {
        checked: options.sponge === 'desaturate',
      }),
    );
  }

  if (EXPOSURE_TOOLS.has(tool)) {
    entries.push(
      sep(),
      sub(
        'Exposure',
        OPACITY_PRESETS.map((pct) =>
          item(`${pct}%`, () => patch({ exposure: pct / 100 }), {
            checked: Math.round((options.exposure ?? 0.5) * 100) === pct,
          }),
        ),
      ),
      sub(
        'Range',
        (['shadows', 'midtones', 'highlights'] as const).map((range) =>
          item(TONE_RANGE_LABELS[range], () => patch({ range }), {
            checked: options.range === range,
          }),
        ),
      ),
    );
  }

  if (SOURCE_TOOLS.has(tool)) {
    entries.push(
      sep(),
      item('Aligned', () => patch({ aligned: !options.aligned }), {
        checked: Boolean(options.aligned),
      }),
      item('Sample All Layers', () => patch({ sampleAllLayers: !options.sampleAllLayers }), {
        checked: Boolean(options.sampleAllLayers),
      }),
    );
  }

  const presets = state.brushPresets;
  if (presets.length > 0 && (tool === 'brush' || tool === 'pencil' || tool === 'eraser')) {
    const tips = tipImages();
    const byCategory = new Map<string, BrushPreset[]>();
    for (const category of BRUSH_PRESET_CATEGORIES) byCategory.set(category, []);
    for (const preset of presets) {
      const key = preset.category ?? 'Custom';
      const list = byCategory.get(key) ?? [];
      list.push(preset);
      byCategory.set(key, list);
    }
    const presetSubs = [...byCategory.entries()]
      .filter(([, items]) => items.length > 0)
      .map(([category, items]) =>
        sub(
          category,
          items.map((preset) =>
            item(preset.name, () => useAppStore.getState().applyBrushPreset(tool, preset), {
              checked: options.presetId === preset.id,
            }),
          ),
        ),
      );

    entries.push(
      sep(),
      sub('Brush Tip', [
        item('Round (default)', () => patch({ tipImageId: null, presetId: null }), {
          checked: !options.tipImageId,
        }),
        sub(
          'Tip Shapes',
          tips.map((tipShape) =>
            item(tipShape.name, () => patch({ tipImageId: tipShape.id }), {
              checked: options.tipImageId === tipShape.id,
            }),
          ),
        ),
        sep(),
        ...presetSubs,
      ]),
    );
  }

  entries.push(
    sep(),
    item('Paint on Layer', () => useAppStore.getState().setPaintTarget('layer'), {
      checked: state.paintTarget === 'layer',
    }),
    item('Paint on Mask', () => useAppStore.getState().setPaintTarget('mask'), {
      checked: state.paintTarget === 'mask',
    }),
    sep(),
    item('Brush Settings…', () => useAppStore.getState().setPanelFlyout('brush')),
  );

  return { id: 'paint', entries };
}

function selectionSection(tool: ToolId, state: AppState): ToolMenuSection {
  const options = state.toolOptions;
  const patch = useAppStore.getState().patchToolOptions;
  const entries: ToolMenuEntry[] = [
    sub(
      'Selection Mode',
      SELECTION_MODES.map(({ value, label }) =>
        item(label, () => patch({ selectionMode: value }), {
          checked: options.selectionMode === value,
        }),
      ),
    ),
    item('Anti-alias', () => patch({ selectionAntialias: !options.selectionAntialias }), {
      checked: options.selectionAntialias,
    }),
    sub(
      'Feather',
      [0, 1, 2, 5, 10, 20].map((px) =>
        item(`${px} px`, () => patch({ selectionFeather: px }), {
          checked: options.selectionFeather === px,
        }),
      ),
    ),
  ];

  if (tool === 'magicWand') {
    entries.push(
      sep(),
      sub(
        'Tolerance',
        [0, 10, 20, 32, 50, 100].map((pct) =>
          item(`${pct}%`, () => patch({ wandTolerance: pct / 100 }), {
            checked: Math.round(options.wandTolerance * 100) === pct,
          }),
        ),
      ),
      item('Contiguous', () => patch({ wandContiguous: !options.wandContiguous }), {
        checked: options.wandContiguous,
      }),
      item('Sample All Layers', () => patch({ wandSampleAllLayers: !options.wandSampleAllLayers }), {
        checked: options.wandSampleAllLayers,
      }),
    );
  }

  if (tool === 'quickSelect') {
    entries.push(
      sep(),
      sub(
        'Brush Size',
        SIZE_PRESETS.map((size) =>
          item(`${size} px`, () => patch({ quickSelectSize: size }), {
            checked: options.quickSelectSize === size,
          }),
        ),
      ),
    );
  }

  if (tool === 'lassoMagnetic') {
    entries.push(
      sep(),
      sub(
        'Width',
        [5, 10, 20, 40, 80].map((width) =>
          item(`${width} px`, () => patch({ magneticWidth: width }), {
            checked: options.magneticWidth === width,
          }),
        ),
      ),
      sub(
        'Contrast',
        [5, 10, 20, 40, 60].map((pct) =>
          item(`${pct}%`, () => patch({ magneticContrast: pct / 100 }), {
            checked: Math.round(options.magneticContrast * 100) === pct,
          }),
        ),
      ),
      sub(
        'Frequency',
        [5, 10, 20, 40, 60].map((frequency) =>
          item(`${frequency}`, () => patch({ magneticFrequency: frequency }), {
            checked: options.magneticFrequency === frequency,
          }),
        ),
      ),
    );
  }

  return { id: 'selection', entries };
}

function vectorSection(state: AppState): ToolMenuSection {
  const options = state.toolOptions;
  const patch = useAppStore.getState().patchToolOptions;
  const modes: { value: VectorMode; label: string }[] = [
    { value: 'shape', label: 'Shape' },
    { value: 'path', label: 'Path' },
    { value: 'pixels', label: 'Pixels' },
  ];
  const ops: { value: PathOp; label: string }[] = [
    { value: 'add', label: 'Combine' },
    { value: 'subtract', label: 'Subtract Front' },
    { value: 'intersect', label: 'Intersect' },
    { value: 'exclude', label: 'Exclude Overlap' },
  ];

  return {
    id: 'vector',
    entries: [
      sub(
        'Mode',
        modes.map(({ value, label }) =>
          item(label, () => patch({ vectorMode: value }), {
            checked: options.vectorMode === value,
          }),
        ),
      ),
      sub(
        'Path Operation',
        ops.map(({ value, label }) =>
          item(label, () => patch({ pathOp: value }), {
            checked: options.pathOp === value,
          }),
        ),
      ),
      sep(),
      item('Fill', () => patch({ shapeFillEnabled: !options.shapeFillEnabled }), {
        checked: options.shapeFillEnabled,
      }),
      item('Stroke', () => patch({ shapeStrokeEnabled: !options.shapeStrokeEnabled }), {
        checked: options.shapeStrokeEnabled,
      }),
    ],
  };
}

function gradientSection(state: AppState): ToolMenuSection {
  const gradient = state.gradient;
  const patch = useAppStore.getState().patchGradient;
  return {
    id: 'gradient',
    entries: [
      item('Edit Gradient…', () => useAppStore.getState().openDialog({ kind: 'gradient' })),
      sep(),
      sub(
        'Style',
        GRADIENT_TYPES.map((type) =>
          item(GRADIENT_TYPE_LABELS[type], () => patch({ type }), {
            checked: gradient.type === type,
          }),
        ),
      ),
      item('Reverse', () => patch({ reverse: !gradient.reverse }), {
        checked: gradient.reverse,
      }),
      item('Dither', () => patch({ dither: !gradient.dither }), {
        checked: gradient.dither,
      }),
      sub(
        'Opacity',
        OPACITY_PRESETS.map((pct) =>
          item(`${pct}%`, () => patch({ opacity: pct / 100 }), {
            checked: Math.round(gradient.opacity * 100) === pct,
          }),
        ),
      ),
    ],
  };
}

function bucketSection(state: AppState): ToolMenuSection {
  const options = state.toolOptions;
  const patch = useAppStore.getState().patchToolOptions;
  return {
    id: 'bucket',
    entries: [
      sub(
        'Tolerance',
        [0, 10, 20, 32, 50, 100].map((pct) =>
          item(`${pct}%`, () => patch({ bucketTolerance: pct / 100 }), {
            checked: Math.round(options.bucketTolerance * 100) === pct,
          }),
        ),
      ),
      item('Contiguous', () => patch({ bucketContiguous: !options.bucketContiguous }), {
        checked: options.bucketContiguous,
      }),
    ],
  };
}

function typeSection(state: AppState): ToolMenuSection {
  const options = state.toolOptions;
  const patch = useAppStore.getState().patchToolOptions;
  return {
    id: 'type',
    entries: [
      sub(
        'Font',
        TEXT_FONT_OPTIONS.slice(0, 12).map(({ value, label }) =>
          item(label, () => patch({ textFontFamily: value }), {
            checked: options.textFontFamily === value,
          }),
        ),
      ),
      sub(
        'Size',
        [12, 18, 24, 36, 48, 72, 120].map((size) =>
          item(`${size} px`, () => patch({ textFontSize: size }), {
            checked: options.textFontSize === size,
          }),
        ),
      ),
      sub(
        'Align',
        TEXT_ALIGN_OPTIONS.map(({ value, label }) =>
          item(label, () => patch({ textAlign: value }), {
            checked: options.textAlign === value,
          }),
        ),
      ),
      sep(),
      item('Bold', () => patch({ textBold: !options.textBold }), {
        checked: options.textBold,
      }),
      item('Italic', () => patch({ textItalic: !options.textItalic }), {
        checked: options.textItalic,
      }),
      sep(),
      item('Character Panel…', () => useAppStore.getState().setPanelFlyout('character')),
    ],
  };
}

function transformSection(state: AppState): ToolMenuSection {
  const mode = state.toolOptions.transformMode;
  const modes: { value: TransformMode; label: string }[] = [
    { value: 'free', label: 'Free' },
    { value: 'skew', label: 'Skew' },
    { value: 'distort', label: 'Distort' },
    { value: 'perspective', label: 'Perspective' },
    { value: 'warp', label: 'Warp' },
  ];
  return {
    id: 'transform',
    entries: [
      ...modes.map(({ value, label }) =>
        item(label, () => setTransformMode(value), { checked: mode === value }),
      ),
      sep(),
      item(
        'Constrain Proportions',
        () =>
          useAppStore.getState().patchToolOptions({
            transformKeepAspect: !state.toolOptions.transformKeepAspect,
          }),
        { checked: state.toolOptions.transformKeepAspect },
      ),
    ],
  };
}

function eyedropperSection(state: AppState): ToolMenuSection {
  const options = state.toolOptions;
  return {
    id: 'eyedropper',
    entries: [
      item(
        'Sample All Layers',
        () =>
          useAppStore.getState().patchToolOptions({
            eyedropperFromComposite: !options.eyedropperFromComposite,
          }),
        { checked: options.eyedropperFromComposite },
      ),
    ],
  };
}

function cropSection(): ToolMenuSection {
  return {
    id: 'crop',
    entries: [
      item('Commit Crop', () => {
        /* Enter is handled by the tool; nudge user via status. */
        useAppStore.getState().setStatus('Press Enter to commit crop, Esc to cancel');
      }),
      item('Cancel Crop', () => {
        useAppStore.getState().setStatus('Press Esc to cancel crop');
      }),
    ],
  };
}

/** Tool-specific options for the canvas right-click menu. */
export function buildToolContextSections(tool: ToolId, state: AppState): ToolMenuSection[] {
  if (isPaintTool(tool)) return [paintSection(tool, state)];
  if (SELECTION_TOOLS.has(tool)) return [selectionSection(tool, state)];
  if (VECTOR_TOOLS.has(tool)) return [vectorSection(state)];
  if (tool === 'gradient') return [gradientSection(state)];
  if (tool === 'bucket') return [bucketSection(state)];
  if (tool === 'type') return [typeSection(state)];
  if (tool === 'transform') return [transformSection(state)];
  if (tool === 'eyedropper') return [eyedropperSection(state)];
  if (tool === 'crop') return [cropSection()];
  return [];
}
